<template>
  <ul
    v-if="visible"
    @contextmenu.prevent
    :style="{ top: y + 'px', left: x + 'px' }"
    class="context-menu"
  >
    <!-- Image-specific actions -->
    <li v-if="isImage" @click="openImageNewTab">
      <span>Open Image in New Tab</span>
      <span class="hotkey"></span>
    </li>
    <li v-if="isImage" @click="downloadImage">
      <span>Download Image</span>
    </li>
    <li v-if="isImage" @click="copyImageUrl">
      <span>Copy Image Address</span>
    </li>
    <li v-if="isImage" @click="copyImage">
      <span>Copy Image</span>
    </li>
    <li v-if="isImage" class="separator"></li>

    <!-- Link-specific actions -->
    <li v-if="isLink" @click="openLink">
      <span>Open</span>
      <span class="hotkey"></span>
    </li>
    <li v-if="isLink" @click="openLinkNewTab">
      <span>Open in New Tab</span>
      <span class="hotkey"></span>
    </li>
    <li class="separator" v-if="isLink"></li>
    <li @click="copy" :class="{ disabled: !canCopy }">
      <span>Copy</span>
      <span class="hotkey">{{ hotkeys.copy }}</span>
    </li>
    <li @click="cut" :class="{ disabled: !canCut }">
      <span>Cut</span>
      <span class="hotkey">{{ hotkeys.cut }}</span>
    </li>
    <li @click="paste" :class="{ disabled: !canPaste }">
      <span>Paste</span>
      <span class="hotkey">{{ hotkeys.paste }}</span>
    </li>

    <li class="separator"></li>

    <li @click="goBack" :class="{ disabled: !canGoBack }">
      <span>Back</span>
      <span class="hotkey">{{ hotkeys.back }}</span>
    </li>
    <li @click="goForward" :class="{ disabled: !canGoForward }">
      <span>Forward</span>
      <span class="hotkey">{{ hotkeys.forward }}</span>
    </li>
    <li @click="refresh">
      <span>Refresh</span>
      <span class="hotkey">{{ hotkeys.refresh }}</span>
    </li>
    <li @click="copyPageURL">
      <span>Copy Page URL</span>
      <span class="hotkey"></span>
    </li>
    <li @click="printPage">
      <span>Print</span>
      <span class="hotkey">{{ hotkeys.print }}</span>
    </li>
  </ul>
</template>

<script>
export default {
  name: "RightClickMenu",
  data() {
    return {
      isMac: navigator.userAgentData
        ? navigator.userAgentData.platform === "macOS"
        : /Mac|iPhone|iPad|iPod/.test(navigator.userAgent),
      visible: false,
      x: 0,
      y: 0,
      target: null,
      selection: "",
      clipboardText: "",
      linkElement: null,
      imageUrl: null,
      imageElement: null,
    };
  },
  computed: {
    hotkeys() {
      if (this.isMac) {
        return {
          copy: "⌘ + C",
          cut: "⌘ + X",
          paste: "⌘ + V",
          back: "⌘ + [",
          forward: "⌘ + ]",
          refresh: "⌘ + R",
          print: "⌘ + P",
          saveAs: "⌘ + S",
        };
      } else {
        return {
          copy: "Ctrl + C",
          cut: "Ctrl + X",
          paste: "Ctrl + V",
          back: "Alt + Left",
          forward: "Alt + Right",
          refresh: "F5",
          print: "Ctrl + P",
          saveAs: "Ctrl + S",
        };
      }
    },
    isLink() {
      return !!this.linkElement;
    },
    isImage() {
      return !!this.imageUrl;
    },
    canGoBack() {
      return window.history.length > 1;
    },
    canGoForward() {
      return false; // Add logic if you want forward state
    },
    canCopy() {
      if (!this.target) return false;
      if (this.selection) return true;
      const text = this.target.innerText || this.target.value || "";
      return text.trim().length > 0;
    },
    canCut() {
      if (!this.target) return false;
      if (!this.selection) return false;
      const tag = this.target.tagName.toLowerCase();
      return tag === "input" || tag === "textarea";
    },
    canPaste() {
      if (!this.target) return false;
      const tag = this.target.tagName.toLowerCase();
      return (
        (tag === "input" || tag === "textarea") && this.clipboardText.length > 0
      );
    },
  },
  methods: {
    async openMenu({ event }) {
      this.target = event.target;
      this.selection = window.getSelection().toString();
      //link
      this.linkElement = this.target.closest("a[href]");

      //img
      const img = this.target.closest("img");
      this.imageElement = null;
      this.imageUrl = null;

      if (img && img.src) {
        this.imageElement = img;
        this.imageUrl = img.src;
      } else {
        // 2. Background-image detection
        const bg = window.getComputedStyle(this.target).backgroundImage;
        if (bg && bg !== "none") {
          const match = bg.match(/url\(["']?(.*?)["']?\)/);
          if (match) {
            this.imageElement = null;
            this.imageUrl = match[1];
          }
        }
      }

      // Initial click position
      let clickX = event.clientX;
      let clickY = event.clientY;

      // Temporarily show menu off-screen to measure size
      this.visible = true;
      await this.$nextTick(); // wait for DOM update
      const menu = this.$el;
      const menuWidth = menu.offsetWidth + 160;
      const menuHeight = menu.offsetHeight;

      // Adjust horizontal position if menu would overflow

      if (clickX + menuWidth > window.innerWidth) {
        this.x = window.innerWidth - menuWidth - 8; // 8px padding
      } else {
        this.x = clickX;
      }

      const viewportBottom = window.scrollY + window.innerHeight;

      if (clickY + menuHeight > viewportBottom) {
        this.y = viewportBottom - menuHeight - 8;
      } else {
        this.y = clickY;
      }

      // Read clipboard for Paste enable/disable
      try {
        this.clipboardText = await navigator.clipboard.readText();
      } catch {
        this.clipboardText = "";
      }

      // Listen for hotkeys
      document.addEventListener("keydown", this.handleHotkeys);

      // Close menu on click outside
      const closeHandler = () => {
        this.visible = false;
        document.removeEventListener("click", closeHandler);
        document.removeEventListener("keydown", this.handleHotkeys);
      };
      document.addEventListener("click", closeHandler);
    },

    copy() {
      if (!this.canCopy) return;
      let text =
        this.selection || this.target.innerText || this.target.value || "";
      navigator.clipboard.writeText(text).then(() => {});
      this.visible = false;
    },

    cut() {
      if (!this.canCut) return;
      document.execCommand("cut");
      this.visible = false;
    },

    async paste() {
      if (!this.canPaste) return;
      const text = await navigator.clipboard.readText();
      const start = this.target.selectionStart;
      const end = this.target.selectionEnd;
      const value = this.target.value;
      this.target.value = value.slice(0, start) + text + value.slice(end);
      this.visible = false;
    },

    openImageNewTab() {
      window.open(this.imageUrl, "_blank");
      this.visible = false;
    },

    async downloadImage() {
      try {
        const res = await fetch(this.imageUrl);
        const blob = await res.blob();

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = this.imageUrl.split("/").pop() || "image";

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
      } catch (err) {
        window.open(this.imageUrl, "_blank");
      }

      this.visible = false;
    },

    copyImageUrl() {
      navigator.clipboard.writeText(this.imageUrl);
      this.visible = false;
    },

    async copyImage() {
      try {
        const res = await fetch(this.imageUrl);
        const blob = await res.blob();
        await navigator.clipboard.write([
          // eslint-disable-next-line no-undef
          new ClipboardItem({ [blob.type]: blob }),
        ]);
      } catch {
        alert("Copy image not supported in this browser.");
      }
      this.visible = false;
    },

    goBack() {
      if (!this.canGoBack) return;
      window.history.back();
      this.visible = false;
    },

    goForward() {
      if (!this.canGoForward) return;
      window.history.forward();
      this.visible = false;
    },

    refresh() {
      this.visible = false;
      window.location.reload();
    },

    copyPageURL() {
      navigator.clipboard.writeText(window.location.href).then(() => {});
      this.visible = false;
    },

    openLink() {
      if (!this.isLink) return;
      window.location.href = this.linkElement.href;
      this.visible = false;
    },
    openLinkNewTab() {
      if (!this.isLink) return;
      window.open(this.linkElement.href, "_blank");
      this.visible = false;
    },

    printPage() {
      this.visible = false;
      setTimeout(() => window.print(), 300);
    },
  },
};
</script>

<style scoped>
.context-menu {
  position: fixed;
  z-index: 10000000000;
  width: 220px;
  padding: 4px 0;
  list-style: none;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px) saturate(180%);
  -webkit-backdrop-filter: blur(10px) saturate(180%);
  border: 1px solid rgba(200, 200, 200, 0.4);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: 14px;
  color: #222;
  user-select: none;
  -webkit-touch-callout: none;
}

.context-menu::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  pointer-events: none;
}

.context-menu li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease;
}

.context-menu li:hover {
  background: rgba(0, 0, 0, 0.05);
}

.separator {
  border-top: 1px solid #ccc;
  margin: 5px 0;
  pointer-events: none;
}

.context-menu .disabled {
  color: #878b90;
  pointer-events: none;
}
.context-menu .disabled:hover {
  background: inherit !important;
}
.context-menu li .hotkey {
  color: #878b90;
  float: right;
}
.context-menu li:hover .hotkey {
  color: #000 !important;
}
.context-menu li.disabled:hover .hotkey {
  color: #878b90 !important;
}
</style>
